/**
 * WebSocket client.
 *
 * Connection sequence: fetch a single-use ticket over authenticated HTTP,
 * open the socket, send the ticket as the first frame, then subscribe to
 * channels. The access token never appears in a URL, because query strings end
 * up in proxy and server logs.
 *
 * Reconnection uses exponential backoff with jitter. The jitter matters more
 * than it looks: when the gateway restarts, every open browser reconnects at
 * once, and a fixed delay would have them all arrive together, repeatedly.
 *
 * On every (re)connect the client raises `onResync`. The socket is a
 * notification channel, never a state store, so the UI refetches authoritative
 * state over REST and does not try to replay missed events.
 */

import type { RealtimeEvent } from '@/api/types';

export type ConnectionState = 'idle' | 'connecting' | 'open' | 'closed';

export interface RealtimeClientOptions {
  url: string;
  fetchTicket: () => Promise<string>;
  channels: () => string[];
  onEvent: (event: RealtimeEvent) => void;
  onState: (state: ConnectionState) => void;
  onResync: () => void;
  /** Injected for tests; defaults to the platform implementations. */
  socketFactory?: (url: string) => WebSocket;
  now?: () => number;
  setTimeoutFn?: typeof setTimeout;
  maxBackoffMs?: number;
}

const BASE_BACKOFF_MS = 500;
const DEFAULT_MAX_BACKOFF_MS = 30_000;

/** Codes the gateway uses to say "get a new ticket", not "stop trying". */
const REAUTH_CODES = new Set([4001, 4002, 4003]);

export class RealtimeClient {
  private socket: WebSocket | null = null;
  private attempt = 0;
  private stopped = true;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private readonly options: Required<Omit<RealtimeClientOptions, 'socketFactory'>> &
    Pick<RealtimeClientOptions, 'socketFactory'>;

  constructor(options: RealtimeClientOptions) {
    this.options = {
      maxBackoffMs: DEFAULT_MAX_BACKOFF_MS,
      now: () => Date.now(),
      setTimeoutFn: setTimeout,
      ...options,
    } as Required<Omit<RealtimeClientOptions, 'socketFactory'>> &
      Pick<RealtimeClientOptions, 'socketFactory'>;
  }

  get state(): ConnectionState {
    if (this.stopped) return 'idle';
    if (!this.socket) return 'connecting';
    return this.socket.readyState === WebSocket.OPEN ? 'open' : 'connecting';
  }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    this.attempt = 0;
    void this.connect();
  }

  stop(): void {
    this.stopped = true;
    if (this.timer) this.options.setTimeoutFn === setTimeout ? clearTimeout(this.timer) : undefined;
    this.timer = null;
    this.socket?.close(1000, 'client stopped');
    this.socket = null;
    this.options.onState('idle');
  }

  /** Subscribe to additional channels on a live connection. */
  subscribe(channels: string[]): void {
    this.send({ op: 'subscribe', channels });
  }

  unsubscribe(channels: string[]): void {
    this.send({ op: 'unsubscribe', channels });
  }

  private send(payload: unknown): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(payload));
    }
  }

  private async connect(): Promise<void> {
    if (this.stopped) return;
    this.options.onState('connecting');

    let ticket: string;
    try {
      ticket = await this.options.fetchTicket();
    } catch {
      // No ticket means no realtime. REST still works, so the UI degrades to
      // polling rather than appearing broken.
      this.scheduleReconnect();
      return;
    }
    if (this.stopped) return;

    const socket = this.options.socketFactory
      ? this.options.socketFactory(this.options.url)
      : new WebSocket(this.options.url);
    this.socket = socket;

    socket.onopen = () => {
      socket.send(JSON.stringify({ op: 'auth', ticket }));
    };

    socket.onmessage = (message: MessageEvent) => {
      let payload: Record<string, unknown>;
      try {
        payload = JSON.parse(String(message.data)) as Record<string, unknown>;
      } catch {
        return;
      }
      this.handle(payload);
    };

    socket.onclose = (event: CloseEvent) => {
      this.socket = null;
      this.options.onState('closed');
      if (this.stopped || event.code === 1000) return;
      // A reauth close is expected roughly every access-token lifetime, so it
      // reconnects immediately rather than backing off.
      if (REAUTH_CODES.has(event.code)) this.attempt = 0;
      this.scheduleReconnect();
    };

    socket.onerror = () => {
      /* onclose always follows; handled there */
    };
  }

  private handle(payload: Record<string, unknown>): void {
    switch (payload.event) {
      case 'CONNECTED':
        this.attempt = 0;
        this.options.onState('open');
        this.subscribe(this.options.channels());
        if (payload.resync) this.options.onResync();
        return;
      case 'AUTH_FAILED':
      case 'SESSION_REVOKED':
      case 'REAUTH_REQUIRED':
      case 'SUBSCRIBED':
      case 'UNSUBSCRIBED':
      case 'PONG':
      case 'ERROR':
        return;
      default:
        if (typeof payload.seq === 'number' && typeof payload.channel === 'string') {
          this.options.onEvent(payload as unknown as RealtimeEvent);
        }
    }
  }

  private scheduleReconnect(): void {
    if (this.stopped) return;
    const exponential = Math.min(
      BASE_BACKOFF_MS * 2 ** this.attempt,
      this.options.maxBackoffMs,
    );
    // Full jitter: without it, every browser reconnects in lockstep after a
    // gateway restart and hammers it on the same schedule.
    const delay = Math.random() * exponential;
    this.attempt += 1;
    this.timer = this.options.setTimeoutFn(() => void this.connect(), delay);
  }
}

/** Resolve the gateway URL, defaulting to the same origin as the page. */
export function gatewayUrl(): string {
  const configured = import.meta.env?.VITE_WS_URL as string | undefined;
  if (configured && /^wss?:\/\//.test(configured)) return configured;
  const path = configured || '/ws';
  const scheme = globalThis.location?.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${globalThis.location?.host ?? 'localhost'}${path}`;
}
